import { ChangeDetectionStrategy, Component, input, output } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';

/**
 * A single row in a card list: a leading icon in a rounded tile, a title with an optional
 * subtitle, and a trailing action area. Use for the compact entity lists shown inside
 * `xemex-card` (partnerships, devices, locations, audit entries, …).
 *
 * Project trailing controls (status text, delete button, chevron, timestamp) into the
 * `[listRowActions]` slot, and any extra body content (extra detail lines, inline links) as
 * default content — it renders directly under the subtitle.
 *
 * Project chips (e.g. `xemex-status-chip`) into `[listRowBadges]` to show them inline with the
 * subtitle on the same line, wrapping together if space is tight.
 *
 * The leading slot is an icon tile by default ({@link icon}); pass content into
 * `[listRowLeading]` instead (e.g. a `xemex-tag`) to replace it, leaving `icon` unset.
 *
 * Set {@link clickable} to make the whole row a hover target that emits {@link rowClick}; action
 * buttons in the slot should call `event.stopPropagation()` so they don't also trigger the row.
 * Use `align="start"` for rows whose body wraps to multiple lines (e.g. audit entries).
 */
@Component({
  selector: 'xemex-list-row',
  standalone: true,
  imports: [MatIconModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './list-row.component.html',
})
export class ListRowComponent {
  icon = input<string>();
  title = input.required<string>();
  subtitle = input<string>();
  clickable = input<boolean>(false);
  align = input<'center' | 'start'>('center');
  /** Tighter vertical padding for dense lists (e.g. dashboard widgets) where many rows need to
   * fit without feeling sparse. */
  dense = input<boolean>(false);
  /** Icon-tile style — `neutral` (default) is the plain gray tile used for most lists;
   * `accent` is the brand-teal highlight tile (matches the device/dashboard header icon tiles)
   * for lists where the icon itself is the primary way to tell entries apart (e.g. a mixed
   * recently-used list of devices/locations/partners). */
  iconVariant = input<'neutral' | 'accent'>('neutral');
  rowClick = output<void>();

  onClick(): void {
    if (this.clickable()) {
      this.rowClick.emit();
    }
  }
}
